import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { X, CaretLeft, CaretRight } from "@phosphor-icons/react";
import { fileUrl } from "../lib/api";
import useBodyScrollLock from "../lib/useBodyScrollLock";

/**
 * Lightbox de imagens em tela cheia.
 * - Mostra a foto inteira (object-contain) sobre fundo escuro.
 * - Navega entre as fotos: setas na tela, teclado (←/→), e swipe no mobile.
 * - Fecha no X, no clique do fundo ou tecla Esc.
 */
export default function ImageLightbox({ images = [], index = 0, onIndexChange, onClose }) {
  const touchStartX = useRef(null);
  const total = images.length;
  useBodyScrollLock(true);

  const go = (dir) => {
    if (total <= 1) return;
    const next = (index + dir + total) % total;
    onIndexChange?.(next);
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
      else if (e.key === "ArrowRight") go(1);
      else if (e.key === "ArrowLeft") go(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  if (total === 0) return null;

  const onTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(dx) > 50) go(dx < 0 ? 1 : -1);
    touchStartX.current = null;
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[120] bg-black/95 flex items-center justify-center select-none animate-[fadeIn_.15s_ease-out]"
      onClick={(e) => e.target === e.currentTarget && onClose?.()}
      data-testid="image-lightbox"
      style={{ paddingTop: "env(safe-area-inset-top)", paddingBottom: "env(safe-area-inset-bottom)" }}
    >
      <button
        onClick={(e) => { e.stopPropagation(); onClose?.(); }}
        className="absolute right-3 z-10 w-11 h-11 rounded-full bg-white/15 hover:bg-white/30 text-white grid place-items-center backdrop-blur transition-colors"
        style={{ top: "calc(env(safe-area-inset-top, 0px) + 12px)" }}
        aria-label="Fechar"
        data-testid="lightbox-close"
      >
        <X size={22} weight="bold" />
      </button>

      {total > 1 && (
        <div
          className="absolute left-1/2 -translate-x-1/2 z-10 text-white/90 text-sm font-bold bg-black/40 px-3 py-1 rounded-full"
          style={{ top: "calc(env(safe-area-inset-top, 0px) + 16px)" }}
          data-testid="lightbox-counter"
        >
          {index + 1} / {total}
        </div>
      )}

      <img
        src={fileUrl(images[index])}
        alt=""
        onClick={(e) => e.stopPropagation()}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        className="max-w-[95vw] max-h-[88vh] object-contain"
        data-testid="lightbox-image"
      />

      {total > 1 && (
        <>
          <button
            onClick={(e) => { e.stopPropagation(); go(-1); }}
            className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full bg-white/15 hover:bg-white/30 text-white grid place-items-center backdrop-blur transition-colors"
            aria-label="Foto anterior"
            data-testid="lightbox-prev"
          >
            <CaretLeft size={26} weight="bold" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); go(1); }}
            className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full bg-white/15 hover:bg-white/30 text-white grid place-items-center backdrop-blur transition-colors"
            aria-label="Próxima foto"
            data-testid="lightbox-next"
          >
            <CaretRight size={26} weight="bold" />
          </button>

          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-10 flex gap-2 max-w-[92vw] overflow-x-auto px-2 py-1" data-scroll-lock-allow="true" onClick={(e) => e.stopPropagation()}>
            {images.map((p, i) => (
              <button
                key={p + i}
                onClick={() => onIndexChange?.(i)}
                className={`w-12 h-12 rounded-md overflow-hidden flex-shrink-0 ring-2 transition-all ${i === index ? "ring-[#FF6B35] opacity-100" : "ring-transparent opacity-50 hover:opacity-90"}`}
                data-testid={`lightbox-thumb-${i}`}
              >
                <img src={fileUrl(p)} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </>
      )}
    </div>,
    document.body
  );
}
